import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { Dispatch } from 'redux';
import FileServices from 'services/FileServices';
import { CacheTypes } from 'modules/types/mobileCaching';
import {
  problemsDownloadingSelector,
  problemsRequestStateSelector
} from 'store/medicalHistory/problems/selectors';
import { messageShare } from 'store/messaging/actions';
import { RootState, Thunk } from 'store/types';
import { DFDHeaders } from 'serverTypes/authentication';
import { DFDHeadersObject, DispatcherEndpoint, DispatcherResource } from 'modules/types/common';
import { getMedicalHistoryEndpointGenerator } from '../common';
import { TransmitSources } from '../../transmit';
import { Problem } from '../types';

/**
 * Get Problems
 */
export const FETCH_PROBLEMS_CACHE_SUCCESS = 'PROBLEMS.FETCH_PROBLEMS_CACHE_SUCCESS';

export const FETCH_PROBLEMS = 'PROBLEMS.FETCH_PROBLEMS';
export const FETCH_PROBLEMS_SUCCESS = 'PROBLEMS.FETCH_PROBLEMS_SUCCESS';
export const FETCH_PROBLEMS_FAIL = 'PROBLEMS.FETCH_PROBLEMS_FAIL';
const getEndpoint = getMedicalHistoryEndpointGenerator(DispatcherResource.CONDITIONS);

export const fetchProblems = (useRequestState = true) => ({
  type: FETCH_PROBLEMS,
  payload: {
    useRequestState,
    requestStateSelector: problemsRequestStateSelector,
    cacheType: CacheTypes.PROBLEMS,
    cacheRetrievalSuccessAction: FETCH_PROBLEMS_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getEndpoint()
    }
  }
});

export const createReplyFromProblems = (
  activeProblems: Problem[],
  remissionProblems: Problem[],
  resolvedProblems: Problem[]
) => {
  const activeText =
    activeProblems.length >= 1
      ? activeProblems
          .map(
            prob =>
              `\n${prob.name} is ${prob.status} and ${prob.confirmation}\n(Onset: ${prob.onsetDate}, last updated ${prob.lastUpdated} by ${prob.provider})\n`
          )
          .join(`\n`)
      : '\nNo current active problems\n';

  const remissionText =
    remissionProblems.length >= 1
      ? remissionProblems
          .map(
            prob =>
              `\n${prob.name} is ${prob.status} and ${prob.confirmation}\n(Onset: ${prob.onsetDate}, last updated ${prob.lastUpdated} by ${prob.provider})\n`
          )
          .join(`\n`)
      : `\nNo current problems in remission\n`;

  const resolvedText =
    resolvedProblems.length >= 1
      ? resolvedProblems
          .map(
            prob =>
              `\n${prob.name} is ${prob.status} and ${prob.confirmation}\n(Onset: ${prob.onsetDate}, last updated ${prob.lastUpdated} by ${prob.provider})\n`
          )
          .join(`\n`)
      : '\nNo current resolved problems\n';

  const replyText = `\nProblem List\n\n-Active Problems: \n${activeText}\n-Remission Problems: \n${remissionText}\n-Resolved Problems: \n${resolvedText}\n`;

  return replyText;
};

export const replyToProblems = (
  activeProblems: Problem[],
  remissionProblems: Problem[],
  resolvedProblems: Problem[],
  returnBackTo?: string
): Thunk => dispatch => {
  if (!activeProblems && !remissionProblems && !resolvedProblems) {
    return;
  }

  const problems = [...activeProblems, ...remissionProblems, ...resolvedProblems];

  const params = {
    subject: 'Medical History',
    readonly: createReplyFromProblems(activeProblems, remissionProblems, resolvedProblems),
    transmitParams: {
      source: TransmitSources.Problems,
      payload: problems,
      returnBackTo
    }
  };

  dispatch(messageShare(params));
};

export const SHARE_PROBLEMS = 'PROBLEMS.SHARE_PROBLEMS';
export const SHARE_PROBLEMS_SUCCESS = 'PROBLEMS.SHARE_PROBLEMS_SUCCESS';
export const SHARE_PROBLEMS_FAIL = 'PROBLEMS.SHARE_PROBLEMS_FAIL';

export const shareProblems = (
  problems: Problem[],
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_PROBLEMS,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getEndpoint(DispatcherEndpoint.TRANSMIT),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Problems
 */
export const DOWNLOAD_PROBLEMS = 'PROBLEMS.DOWNLOAD_PROBLEMS';
export const DOWNLOAD_PROBLEMS_SUCCESS = 'PROBLEMS.DOWNLOAD_PROBLEMS_SUCCESS';
export const DOWNLOAD_PROBLEMS_FAIL = 'PROBLEMS.DOWNLOAD_PROBLEMS_FAIL';

export const downloadProblemsList = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  // Check for in flight downloads if there are any then return
  if (problemsDownloadingSelector(state) === true) {
    return;
  }
  dispatch({ type: DOWNLOAD_PROBLEMS, payload: {} });

  const timestamp = Date.now();

  try {
    const uri = getEndpoint(DispatcherEndpoint.DOWNLOAD, true);

    const headers: DFDHeadersObject = {
      [DFDHeaders.accept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, uri)
    };

    const response = await FileServices.download(`problems-list-${timestamp}.pdf`, uri, headers);

    dispatch({ type: DOWNLOAD_PROBLEMS_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_PROBLEMS_FAIL, error: err });
  }
};
